import React, {ChangeEvent, useRef, useState} from "react";
import cx from "clsx";
import {SvgIcon} from "@montana/shared/svg-icon";
import {Button} from "@montana/shared/button";
import {IButtonBaseProps} from "@montana/shared/button/button";
import {MobileAndTabletPortrait} from "@montana/shared/responsive";
import {IFieldOptionValue} from "../form/field/field";

/**
 * @author SEB
 *
 * @description
 * Can be used outside forms
 *
 * @example
 * <Dropdown
 *      options={options}
 *      value={value}
 *      onChange={(newValue): void => setValue(newValue)}
 *      isValueDisplayed={true}
 *      id={id}
 *      className={className}
 *      required={true}
 *      disabled={true}
 *      placeholder={placeholder}
 * />
 */

export interface IDropdownProps {
    options: IFieldOptionValue[];
    isValueDisplayed?: boolean;
    placeholder?: string;
    onChange?: Function;
    id?: string;
    value?: string | number;
    className?: string;
    required?: boolean;
    disabled?: boolean;
    inline?: boolean;
    label?: string;
    direction?: 'up' | 'down';
}

export const Dropdown = (props: IDropdownProps) => {

    const {
        options,
        isValueDisplayed,
        placeholder,
        onChange,
        id,
        value,
        className,
        required,
        disabled,
        inline,
        label,
        direction = 'down'
    } = props;

    const defaultValue: IFieldOptionValue | undefined = value ? options.find(option => option.value === value) : (placeholder ? undefined : options[0]);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const [opened, setOpened] = useState(false);
    const [selectedOption, setSelectedOption] = useState<IFieldOptionValue | undefined>(defaultValue);
    const [styleObj, setStyleObject] = useState<React.CSSProperties>({});

    const handleClick = (e: MouseEvent) => {
        // Check if target element is contained within this component and return if it is
        if (dropdownRef.current?.contains(e.target as Node)) {
            return;
        }
        // If target element is outside this component, close the dropdown
        closeDropdown();
    };

    const toggleDropdown = () => {
        if (disabled) {
            return;
        }
        if (opened) {
            closeDropdown();
        } else {
            openDropdown();
        }
    };

    const openDropdown = () => {
        setStyleObject({zIndex: 999});
        setOpened(true);

        document.addEventListener('mousedown', handleClick, false);
    };

    const closeDropdown = () => {
        setStyleObject({});
        setOpened(false);

        document.removeEventListener('mousedown', handleClick, false);
    };

    const selectOption = (option: IFieldOptionValue, index: number) => {
        setSelectedOption(option);
        setOpened(false);
        if (onChange) {
            onChange(option.value, option.text, index);
        }
    };

    const onChangeMobile = (e: ChangeEvent<HTMLSelectElement>) => {
        const index = options.findIndex(option => option.value === e.target.value)
        const option: IFieldOptionValue = options[index];
        selectOption(option, index);
    };

    return (
        <div
            className={cx('dropdown__container', (className || ''), {
                'dropdown__container--active': opened,
                'dropdown__container--disabled': disabled,
                'dropdown__container--inline': inline
            })}
            style={styleObj}
            ref={dropdownRef}
        >
            <MobileAndTabletPortrait>
                <select
                    className="dropdown__button"
                    onChange={onChangeMobile}
                    value={selectedOption?.value}
                    id={id}
                    required={required}
                    disabled={disabled}
                >
                    {options.map((option, index) => (
                        <option key={index} value={option.value}>
                            {option.text}
                        </option>
                    ))}
                </select>
            </MobileAndTabletPortrait>
            <button
                className="dropdown__button"
                type="button"
                onClick={toggleDropdown}
                disabled={disabled}
            >
                <div className={cx('dropdown__button__text', {'dropdown__button__text--placeholder': !selectedOption})}>
                    {label &&
                        <span className="dropdown__button__text__label">{label}</span>
                    }
                    <span className="dropdown__button__text__value">
                        {selectedOption ? (isValueDisplayed ? selectedOption.value : selectedOption.element ? selectedOption.element : selectedOption.text) : placeholder}
                    </span>
                </div>

                {opened ? (
                    <SvgIcon className="select__icon" iconName="chevron_up"/>
                ) : (
                    <SvgIcon className="select__icon" iconName="chevron_down"/>
                )}
            </button>

            {opened && (
                <ul className={cx('dropdown__options', {
                    'dropdown__options--upward': direction === 'up'
                })}>
                    {options.map((option, index) => (
                        <li key={index}>
                            <button
                                className="dropdown__option"
                                type="button"
                                onClick={() => selectOption(option, index)}
                            >
                                {selectedOption?.value === option.value && (
                                    <SvgIcon className="dropdown__option__checkmark" iconName="checkmark"/>
                                )}
                                <span className="dropdown__option__text">{option.element ? option.element : option.text}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};


/**
 * @author SEB
 *
 * @description
 * A button with an integrated dropdown. It inherits all props from Button and Dropdown and takes a label as its button text
 *
 * @example
 * <ButtonDropdown
 *      label={label}
 *      options={options}
 *      value={value}
 *      onChange={(newValue): void => setValue(newValue)}
 *      isValueDisplayed={true}
 *      id={id}
 *      className={className}
 *      required={true}
 *      disabled={true}
 *      placeholder={placeholder}
 * />
 */

interface IButtonDropdownProps extends IDropdownProps, IButtonBaseProps {
    label: string;
}

export const ButtonDropdown = (props: IButtonDropdownProps) => {

    const {
        label,
        className,
        ...dropdownProps
    } = props;

    return (
        <div className="button-dropdown">
            <Button {...props} className={`button-dropdown ${className || ''}`.trim()} text={label}/>
            <Dropdown {...dropdownProps} />
        </div>
    );
}

/**
 * @author SEB
 *
 * @description
 * A dropdown with a label next to it describing what you're selecting
 *
 * @example
 * <LinkDropdown
 *      label={label}
 *      options={options}
 *      value={value}
 *      onChange={(newValue): void => setValue(newValue)}
 *      isValueDisplayed={true}
 *      id={id}
 *      className={className}
 *      required={true}
 *      disabled={true}
 *      placeholder={placeholder}
 * />
 */
interface ILinkDropdownProps extends IDropdownProps {
    label: string;
}

export const LinkDropdown = (props: ILinkDropdownProps) => {

    const {label} = props;

    return (
        <div className="link-dropdown__container">
            <div className="link-dropdown__label">{label}:</div>
            <Dropdown {...props} />
        </div>
    );
}

/**
 * @author SEB
 *
 * @description
 * A dropdown inlined with a border as form elements. Also used inside the FormSelect
 *
 * @example
 * <UIDropdown
 *      placeholder={label}
 *      options={options}
 *      value={value}
 *      onChange={(newValue): void => setValue(newValue)}
 *      isValueDisplayed={true}
 *      id={id}
 *      className={className}
 *      required={true}
 *      disabled={true}
 *      placeholder={placeholder}
 * />
 */
export const UIDropdown = (props: IDropdownProps) => {

    const {disabled, inline} = props;

    return (
        <div className={cx('ui-dropdown__container', {'dropdown--disabled': disabled, 'dropdown--inline': inline})}>
            <Dropdown {...props} />
        </div>
    );
}


